import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import RegisterLayout from '@/registration/component/Register/Layout';
import { UiSpinner, UiText, UiVStack } from '@/lib/ui';
import { isError, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { cartQueryKey, loadOrder, type Order, orderQueryKey, checkTransaction } from '@/api/registration';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import BaseMessageBarError from '@/base/MessageBar/Error';

const PAID = 'Paid';
const STATUS_APPROVED = 'Approved';
const PaymentProcessing: FC = () => {
  const queryClient = useQueryClient();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { toNextStepWithSteps, toNextStep } = useRegisterSteps();
  const [searchParams] = useSearchParams();
  const { eventId } = useRegisterRoute();
  const registrationQuery = useRegistrationQuery(eventId);
  const orderHashParam = searchParams.get('orderHash') ?? registrationQuery?.data?.orderHash;
  const registrationId = registrationQuery?.data?.id;
  const [isFetch, setIsFetch] = useState<boolean>(!!orderHashParam);
  const [shouldCheckTx, setShouldCheckTx] = useState(false);
  const updateCountRef = useRef(0);

  const reloadAndToNextStep = useCallback(async () => {
    const data = await registrationQuery.refetch();
    const steps = data.data?.registrationSteps;
    if (steps) {
      toNextStepWithSteps(steps);
    } else {
      toNextStep();
    }
  }, [registrationQuery, toNextStepWithSteps, toNextStep]);

  const { data } = useQuery<ApiResponseSingle<Order>, Error>({
    queryKey: [orderQueryKey, { orderHashParam }],
    queryFn: async () => {
      return await loadOrder(createTenantApiRequest)(orderHashParam ?? '');
    },
    refetchInterval: (order, { state }) => {
      updateCountRef.current = state.dataUpdateCount;

      if (updateCountRef.current > 20) {
        // Refetch 20 times, stop and throw error
        if (!shouldCheckTx) {
          setShouldCheckTx(true);
          return false;
        }

        setIsFetch(false);
        return false;
      }
      const status = order?.item?.status;
      if (isError(order?.errors)) {
        setIsFetch(false);
        return false;
      } else if (status === STATUS_APPROVED || status === PAID) {
        return false;
      }
      return 1500;
    },
    enabled: !!orderHashParam && !isApiPreparing
  });

  useEffect(() => {
    const checkTx = async () => {
      try {
        const response = await checkTransaction(createTenantApiRequest)({
          registrationId: registrationId ?? '',
        });

        if (isError(response.errors)) {
          setIsFetch(false);
        } else {
          const order = await loadOrder(createTenantApiRequest)(orderHashParam ?? '');
          const status = order?.item?.status;
          if (status === STATUS_APPROVED || status === PAID) {
            void reloadAndToNextStep();
            await queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId, }] });
          }
        }
      } catch (error) {
        setIsFetch(false);
      }
    };

    if (shouldCheckTx) {
      void checkTx();
    }
  }, [shouldCheckTx]);

  useEffect(() => {
    const fetchData = async () => {
      const status = data?.item?.status;
      if (status === STATUS_APPROVED || status === PAID) {
        void reloadAndToNextStep();
        await queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId, }] });
      }
    };
    void fetchData();
  }, [data?.item?.status, reloadAndToNextStep]);

  if (isFetch) {
    return (
      <RegisterLayout header={<></>}>
        <UiVStack spacing={4}>
          <UiSpinner />
          <UiText variant={'h6'}>We&apos;re completing your order, do not close this page.</UiText>
        </UiVStack>
      </RegisterLayout>
    );
  }

  return (
    <RegisterLayout header={<></>}>
      <BaseMessageBarError>
        Something went wrong. To prevent potential duplicate payments please avoid attempting to make payment again, but contact support instead.
      </BaseMessageBarError>
    </RegisterLayout>
  );
};

export default PaymentProcessing;
